/**
 * The BFF token broker call shared by Dataverse token providers.
 *
 * Goes through bffFetch DELIBERATELY: a 401 here means the session is gone,
 * and bffFetch hands it to the registered teardown, which wipes the persisted
 * query cache and redirects to /auth/login?reason=expired so Entra actually
 * prompts. This started life as a raw fetch that navigated to a bare
 * /auth/login, which Entra silent-SSO'd straight back into a fresh session
 * (fresh 12-hour cap included) and made the idle timeout invisible: the 8/26
 * "prod never times out" report. Do not "simplify" this back to fetch.
 */

import { bffFetch } from './bffFetch';

export interface DataverseTokenResult {
  accessToken: string;
  /** ISO expiry from the BFF, null when it did not report one. */
  expiresOn: string | null;
}

/**
 * @param org Explicit org id ('prod' | 'dev'): the BFF resolves 'prod' to its
 * DATAVERSE_URL_PROD override when set (the dev app service serving the prod
 * org), else its primary. Older BFFs treat anything but 'dev' as primary.
 * Omitted means the BFF's primary org.
 */
export async function fetchDataverseToken(org?: string): Promise<DataverseTokenResult> {
  const response = await bffFetch(org ? `/auth/token?org=${org}` : '/auth/token');

  if (response.status === 401) {
    // The teardown registered with bffFetch is already navigating; rejecting
    // here only stops the caller from proceeding without a token.
    throw new Error('Authentication required');
  }
  if (!response.ok) {
    throw new Error(`Token fetch failed: ${response.statusText}`);
  }

  const data = (await response.json()) as { accessToken: string; expiresOn?: string | null };
  return { accessToken: data.accessToken, expiresOn: data.expiresOn ?? null };
}
